import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import {
  ShieldCheck,
  Upload,
  Search,
  Download,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileSpreadsheet,
  ArrowUpDown,
  ChevronDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function ScoreBadge({ score, risk }) {
  const colors = {
    deliverable: "bg-green-100 text-green-800 border-green-200",
    risky: "bg-yellow-100 text-yellow-800 border-yellow-200",
    undeliverable: "bg-red-100 text-red-800 border-red-200",
    unknown: "bg-gray-100 text-gray-600 border-gray-200",
  };
  const icons = {
    deliverable: <CheckCircle className="w-3.5 h-3.5" />,
    risky: <AlertTriangle className="w-3.5 h-3.5" />,
    undeliverable: <XCircle className="w-3.5 h-3.5" />,
    unknown: <AlertTriangle className="w-3.5 h-3.5" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${colors[risk] || colors.unknown}`}>
      {icons[risk] || icons.unknown}
      {score}/100
    </span>
  );
}

function TagBadge({ active, label }) {
  if (!active) return null;
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600 border border-gray-200">
      {label}
    </span>
  );
}

export default function EmailVerifyPage() {
  const [mode, setMode] = useState("single"); // single or csv
  const [singleEmail, setSingleEmail] = useState("");
  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvColumns, setCsvColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [skipSmtp, setSkipSmtp] = useState(false);
  const [csvJobId, setCsvJobId] = useState(null);
  const [csvStatus, setCsvStatus] = useState(null);
  const [csvResults, setCsvResults] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortField, setSortField] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [stats, setStats] = useState(null);
  const [useNeverbounce, setUseNeverbounce] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Single email verify
  const handleSingleVerify = async () => {
    if (!singleEmail.trim()) return;
    setSingleLoading(true);
    setError("");
    setSingleResult(null);
    try {
      const { data } = await axios.post(`${API}/api/email-verify/verify`, {
        email: singleEmail.trim(),
        skip_smtp: skipSmtp,
      });
      if (data.results && data.results.length > 0) {
        setSingleResult(data.results[0]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed");
    } finally {
      setSingleLoading(false);
    }
  };

  // CSV file selection and column detection
  const handleFileSelect = async (file) => {
    setCsvFile(file);
    setCsvColumns([]);
    setSelectedColumn("");
    setCsvResults([]);
    setCsvJobId(null);
    setCsvStatus(null);
    setError("");

    // Detect columns via API
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/email-verify/verify-csv-columns`, formData);
      setCsvColumns(data.columns || []);
      // Auto-select if there's an obvious email column
      const emailCol = data.columns?.find(c =>
        /^email$/i.test(c) || /email.*address/i.test(c) || /e.?mail/i.test(c)
      );
      if (emailCol) setSelectedColumn(emailCol);
    } catch (err) {
      setError("Failed to read file columns");
    }
  };

  // Note: verify-csv-columns goes directly to microservice since proxy doesn't exist for that
  // Let me add it to the proxy later. For now use the direct endpoint.

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, []);

  // Start CSV verification
  const handleCsvVerify = async () => {
    if (!csvFile || !selectedColumn) return;
    setCsvLoading(true);
    setCsvResults([]);
    setCsvStatus(null);
    setError("");

    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("email_column", selectedColumn);
    formData.append("skip_smtp", skipSmtp);
    formData.append("use_neverbounce", useNeverbounce);

    try {
      const { data } = await axios.post(`${API}/api/email-verify/verify-csv`, formData);
      setCsvJobId(data.job_id);
      setCsvStatus({ status: "running", progress_pct: 0, total_emails: data.total_emails });
      // Start polling
      startPolling(data.job_id);
    } catch (err) {
      setError(err.response?.data?.detail || "CSV verification failed");
      setCsvLoading(false);
    }
  };

  // Poll job status
  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/email-verify/verify-status/${jobId}`);
        setCsvStatus(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setCsvLoading(false);
          if (data.status === "completed") {
            // Fetch results
            try {
              const res = await axios.get(`${API}/api/email-verify/verify-results/${jobId}`);
              setCsvResults(res.data.results || []);
              setStats(res.data.stats || data.stats);
            } catch {
              setStats(data.stats);
            }
          }
          if (data.status === "failed") {
            setError(data.error_message || "Job failed");
          }
        }
      } catch {
        // ignore polling errors
      }
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Download CSV
  const handleDownload = () => {
    if (!csvJobId) return;
    window.open(`${API}/api/email-verify/verify-csv-download/${csvJobId}`, "_blank");
  };

  // Sort results
  const sortedResults = [...csvResults].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortDir === "asc"
      ? String(aVal || "").localeCompare(String(bVal || ""))
      : String(bVal || "").localeCompare(String(aVal || ""));
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-600" />
            Email Verification
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Verify email deliverability with 11 checks and a 0-100 score
          </p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setMode("single")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            mode === "single"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <Search className="w-4 h-4 inline mr-1.5" />
          Single Email
        </button>
        <button
          onClick={() => setMode("csv")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            mode === "csv"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          <FileSpreadsheet className="w-4 h-4 inline mr-1.5" />
          CSV Upload
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* ─── Single Email Mode ─── */}
      {mode === "single" && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex gap-3">
              <input
                type="email"
                placeholder="Enter email address..."
                value={singleEmail}
                onChange={(e) => setSingleEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSingleVerify()}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
              />
              <button
                onClick={handleSingleVerify}
                disabled={singleLoading || !singleEmail.trim()}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                {singleLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Verify
              </button>
            </div>
            <label className="flex items-center gap-2 mt-3 text-xs text-gray-500">
              <input
                type="checkbox"
                checked={skipSmtp}
                onChange={(e) => setSkipSmtp(e.target.checked)}
                className="rounded border-gray-300"
              />
              Skip SMTP check (faster, Layer 1 only)
            </label>
          </div>

          {/* Single Result Card */}
          {singleResult && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{singleResult.email}</h3>
                  <p className="text-sm text-gray-500">{singleResult.domain}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold" style={{
                    color: singleResult.score >= 70 ? "#16a34a" :
                           singleResult.score >= 40 ? "#ca8a04" : "#dc2626"
                  }}>
                    {singleResult.score}
                  </div>
                  <ScoreBadge score={singleResult.score} risk={singleResult.risk_level} />
                </div>
              </div>

              {/* Check details grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <CheckItem label="Syntax" pass={singleResult.syntax_valid} />
                <CheckItem label="MX Records" pass={singleResult.has_mx} />
                <CheckItem label="SPF/DMARC" pass={singleResult.has_spf_dmarc} />
                <CheckItem label="Not Disposable" pass={!singleResult.is_disposable} />
                <CheckItem label="Not Role-Based" pass={!singleResult.is_role_based} />
                <CheckItem label="Not Free Provider" pass={!singleResult.is_free_provider} />
                <CheckItem label="Not Blacklisted" pass={!singleResult.is_blacklisted} />
                <CheckItem label="Not Catch-All" pass={!singleResult.is_catch_all} />
              </div>

              {/* Additional details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <DetailItem label="SMTP Status" value={singleResult.smtp_result} />
                <DetailItem label="Provider" value={singleResult.smtp_provider} />
                <DetailItem label="Domain Age" value={singleResult.domain_age_days > 0 ? `${singleResult.domain_age_days} days` : "Unknown"} />
                <DetailItem label="MX Hosts" value={singleResult.mx_records?.join(", ") || "None"} />
              </div>

              {singleResult.typo_suggestion && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded-lg text-sm">
                  Typo detected! Did you mean: <strong>{singleResult.typo_suggestion}</strong>?
                </div>
              )}

              {/* NeverBounce Result */}
              {singleResult.neverbounce && (
                <div className={`border rounded-lg p-4 ${
                  singleResult.neverbounce.nb_result === "valid" ? "bg-green-50 border-green-200" :
                  singleResult.neverbounce.nb_result === "invalid" ? "bg-red-50 border-red-200" :
                  singleResult.neverbounce.nb_result === "error" ? "bg-gray-50 border-gray-200" :
                  "bg-yellow-50 border-yellow-200"
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">NeverBounce</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${
                        singleResult.neverbounce.nb_result === "valid" ? "bg-green-100 text-green-800 border-green-200" :
                        singleResult.neverbounce.nb_result === "invalid" || singleResult.neverbounce.nb_result === "disposable" ? "bg-red-100 text-red-800 border-red-200" :
                        singleResult.neverbounce.nb_result === "catchall" ? "bg-yellow-100 text-yellow-800 border-yellow-200" :
                        "bg-gray-100 text-gray-600 border-gray-200"
                      }`}>
                        {singleResult.neverbounce.nb_result === "valid" && <CheckCircle className="w-3.5 h-3.5" />}
                        {(singleResult.neverbounce.nb_result === "invalid" || singleResult.neverbounce.nb_result === "disposable") && <XCircle className="w-3.5 h-3.5" />}
                        {(singleResult.neverbounce.nb_result === "catchall" || singleResult.neverbounce.nb_result === "unknown") && <AlertTriangle className="w-3.5 h-3.5" />}
                        {singleResult.neverbounce.nb_result}
                      </span>
                    </div>
                    {singleResult.neverbounce.nb_execution_time > 0 && (
                      <span className="text-xs text-gray-400">{singleResult.neverbounce.nb_execution_time}ms</span>
                    )}
                  </div>
                  {singleResult.neverbounce.nb_flags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {singleResult.neverbounce.nb_flags.map((flag, i) => (
                        <span key={i} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-white/60 text-gray-600 border border-gray-200">
                          {flag}
                        </span>
                      ))}
                    </div>
                  )}
                  {singleResult.neverbounce.nb_suggested_correction && (
                    <p className="text-xs text-yellow-700 mt-2">
                      NB suggests: <strong>{singleResult.neverbounce.nb_suggested_correction}</strong>
                    </p>
                  )}
                  {singleResult.neverbounce.nb_error && (
                    <p className="text-xs text-gray-500 mt-1">NB unavailable: {singleResult.neverbounce.nb_error}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── CSV Upload Mode ─── */}
      {mode === "csv" && (
        <div className="space-y-4">
          {/* Upload area */}
          {!csvFile && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
            >
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Drop a CSV or Excel file here</p>
              <p className="text-gray-400 text-sm mt-1">or click to browse</p>
              <p className="text-gray-400 text-xs mt-2">Supports: CSV, XLSX, XLS, TSV, TXT</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.tsv,.txt"
                onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                className="hidden"
              />
            </div>
          )}

          {/* File selected — column picker + verify button */}
          {csvFile && !csvLoading && !csvStatus?.status?.match(/completed/) && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-900">{csvFile.name}</p>
                    <p className="text-xs text-gray-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  onClick={() => { setCsvFile(null); setCsvColumns([]); setSelectedColumn(""); }}
                  className="text-gray-400 hover:text-gray-600 text-sm"
                >
                  Change file
                </button>
              </div>

              {csvColumns.length > 0 && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Select the column containing email addresses:
                  </label>
                  <div className="relative">
                    <select
                      value={selectedColumn}
                      onChange={(e) => setSelectedColumn(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">-- Select column --</option>
                      {csvColumns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
                  </div>

                  <label className="flex items-center gap-2 text-xs text-gray-500">
                    <input
                      type="checkbox"
                      checked={skipSmtp}
                      onChange={(e) => setSkipSmtp(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Skip SMTP check (faster, Layer 1 only)
                  </label>

                  <label className="flex items-center gap-2 text-xs text-gray-500">
                    <input
                      type="checkbox"
                      checked={useNeverbounce}
                      onChange={(e) => setUseNeverbounce(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Also verify with NeverBounce API (paid, more accurate)
                  </label>

                  <button
                    onClick={handleCsvVerify}
                    disabled={!selectedColumn}
                    className="w-full px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Verify All Emails
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Progress */}
          {csvLoading && csvStatus && (
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                <span className="font-medium text-gray-900">Verifying emails...</span>
                <span className="text-sm text-gray-500 ml-auto">
                  {csvStatus.verified_count || 0} / {csvStatus.total_emails || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${csvStatus.progress_pct || 0}%` }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {(csvStatus.progress_pct || 0).toFixed(1)}% complete
                {csvStatus.cache_hits > 0 && ` (${csvStatus.cache_hits} from cache)`}
              </p>
            </div>
          )}

          {/* Stats Summary */}
          {stats && csvStatus?.status === "completed" && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Total" value={stats.total} color="gray" />
              <StatCard label="Deliverable" value={stats.deliverable} color="green" />
              <StatCard label="Risky" value={stats.risky} color="yellow" />
              <StatCard label="Undeliverable" value={stats.undeliverable} color="red" />
              <StatCard label="Avg Score" value={Math.round(stats.avg_score || 0)} color="blue" />
            </div>
          )}

          {/* Download + Results Table */}
          {csvStatus?.status === "completed" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">
                  Verification Results
                  {csvResults.length > 0 && <span className="text-gray-400 font-normal ml-2">({csvResults.length} emails)</span>}
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setCsvFile(null); setCsvColumns([]); setCsvResults([]); setCsvStatus(null); setCsvJobId(null); setStats(null); }}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    New Upload
                  </button>
                  <button
                    onClick={handleDownload}
                    className="px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download CSV
                  </button>
                </div>
              </div>

              {/* Results table */}
              {csvResults.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <SortHeader label="Email" field="email" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                          <SortHeader label="Score" field="score" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                          <SortHeader label="Risk" field="risk_level" sortField={sortField} sortDir={sortDir} onClick={toggleSort} />
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">SMTP</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
                          <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sortedResults.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2.5 font-mono text-xs">
                              {r.email}
                              {r.typo_suggestion && (
                                <span className="text-yellow-600 ml-1" title={`Suggested: ${r.typo_suggestion}`}>
                                  (typo?)
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5">
                              <ScoreBadge score={r.score} risk={r.risk_level} />
                            </td>
                            <td className="px-4 py-2.5 text-xs capitalize">{r.risk_level}</td>
                            <td className="px-4 py-2.5 text-xs">{r.smtp_result}</td>
                            <td className="px-4 py-2.5">
                              <div className="flex flex-wrap gap-1">
                                <TagBadge active={r.is_disposable} label="Disposable" />
                                <TagBadge active={r.is_role_based} label="Role" />
                                <TagBadge active={r.is_free_provider} label="Free" />
                                <TagBadge active={r.is_blacklisted} label="Blacklisted" />
                                <TagBadge active={r.is_catch_all} label="Catch-All" />
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-xs capitalize">{r.smtp_provider}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper components
function CheckItem({ label, pass }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
      pass ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
    }`}>
      {pass ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      {label}
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="px-3 py-2 bg-gray-50 rounded-lg">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900 truncate">{value}</div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    green: "bg-green-50 border-green-200 text-green-700",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-700",
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    gray: "bg-gray-50 border-gray-200 text-gray-700",
  };
  return (
    <div className={`border rounded-xl p-4 text-center ${colors[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium mt-1">{label}</div>
    </div>
  );
}

function SortHeader({ label, field, sortField, sortDir, onClick }) {
  return (
    <th
      className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
      onClick={() => onClick(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? "text-blue-500" : "text-gray-300"}`} />
      </span>
    </th>
  );
}
